import { useMemo } from 'react';
import { format } from 'date-fns';
import { TimelineChronologyEntry } from '@/store';

interface UseDateHeaderLogicProps {
  mixedListEntries?: (string | TimelineChronologyEntry | null | undefined)[];
  currentListEntries: TimelineChronologyEntry[];
  isChronologyActive: boolean;
}

export const useDateHeaderLogic = ({
  mixedListEntries,
  currentListEntries,
  isChronologyActive,
}: UseDateHeaderLogicProps) => {
  // Date header mapping and index mapping
  const { entryIdToDateHeaderMap, entryIdToMixedIndexMap } = useMemo(() => {
    const dateHeaderMap = new Map<string, string | null>();
    const indexMap = new Map<string, number>();
    if (!mixedListEntries || mixedListEntries.length === 0)
      return {
        entryIdToDateHeaderMap: dateHeaderMap,
        entryIdToMixedIndexMap: indexMap,
      };

    for (let i = 0; i < mixedListEntries.length; i++) {
      const currentItem = mixedListEntries[i];

      if (typeof currentItem === 'object' && currentItem !== null && 'id' in currentItem) {
        const entryId = currentItem.id;
        indexMap.set(entryId, i);
        if (i > 0) {
          const prevItem = mixedListEntries[i - 1];
          dateHeaderMap.set(entryId, typeof prevItem === 'string' ? prevItem : null);
        } else {
          dateHeaderMap.set(entryId, null);
        }
      }
    }
    return {
      entryIdToDateHeaderMap: dateHeaderMap,
      entryIdToMixedIndexMap: indexMap,
    };
  }, [mixedListEntries]);

  // Entry index mapping
  const entryIdToIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    currentListEntries.forEach((entry, index) => {
      map.set(entry.id, index);
    });
    return map;
  }, [currentListEntries]);

  const shouldShowDateHeaderForEntry = (entryId: string) => {
    return entryIdToDateHeaderMap.get(entryId) || null;
  };

  const renderDateHeader = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const formattedDate = format(date, 'dd MMM yyyy');
      return `Records disclosed ${formattedDate}`;
    } catch {
      return `Records disclosed ${String(dateString)}`;
    }
  };

  return {
    entryIdToDateHeaderMap,
    entryIdToMixedIndexMap,
    entryIdToIndexMap,
    shouldShowDateHeaderForEntry,
    renderDateHeader,
    isChronologyActive,
  };
};
